import { LockKeyIcon, XIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useDialogFocus } from '../hooks';
import { useI18n } from '../i18n';

interface PasteDialogProps {
  readonly initialGitignore: string;
  readonly initialPaths: string;
  readonly onApply: (gitignore: string, paths: string) => void;
  readonly onClose: () => void;
}

export function PasteDialog({
  initialGitignore,
  initialPaths,
  onApply,
  onClose,
}: PasteDialogProps) {
  const { t } = useI18n();
  const [gitignore, setGitignore] = useState(initialGitignore);
  const [paths, setPaths] = useState(initialPaths);
  const dialogRef = useDialogFocus(onClose);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="dialog paste-dialog"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="paste-dialog-title"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="dialog-heading">
          <div>
            <h2 id="paste-dialog-title">{t('paste.title')}</h2>
            <p>{t('paste.description')}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t('actions.close')}
            onClick={onClose}
          >
            <XIcon aria-hidden="true" />
          </button>
        </div>

        <div className="privacy-note">
          <LockKeyIcon aria-hidden="true" />
          <strong>{t('privacy.filesNeverLeave')}</strong>
          <span>{t('privacy.pathsOnly')}</span>
        </div>

        <div className="paste-grid">
          <label className="dialog-field">
            <span>{t('paste.gitignoreLabel')}</span>
            <textarea
              data-dialog-initial-focus
              spellCheck={false}
              value={gitignore}
              onChange={(event) => {
                setGitignore(event.currentTarget.value);
              }}
            />
            <small>{t('paste.gitignoreHelp')}</small>
          </label>
          <label className="dialog-field">
            <span>{t('paste.pathsLabel')}</span>
            <textarea
              spellCheck={false}
              value={paths}
              onChange={(event) => {
                setPaths(event.currentTarget.value);
              }}
            />
            <small>{t('paste.pathsHelp')}</small>
          </label>
        </div>

        <div className="dialog-actions">
          <button className="button" type="button" onClick={onClose}>
            {t('actions.cancel')}
          </button>
          <button
            className="button button-primary"
            type="button"
            onClick={() => {
              onApply(gitignore, paths);
            }}
          >
            {t('actions.analyzeInputs')}
          </button>
        </div>
      </section>
    </div>
  );
}
